import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';
import { extractData, extractDataArray } from '../utils/api-response';
import { normalizeAuthUser } from '../utils/auth-user-normalize';
import type { AuthUser, UpdateUserRequest, UserNotificationPreferences } from '../types/auth.types';
import { BaseHttpService } from './base-http.service';

const USER_PATH = API_CONFIG.paths.user;

@Injectable({ providedIn: 'root' })
export class UserApiService extends BaseHttpService {
  /**
   * Fetches a user by username (`GET /api/User/username/:username`).
   * Use to read notification preferences and profile fields returned by the API.
   */
  getByUsername(username: string): Observable<AuthUser> {
    const segment = encodeURIComponent(username.trim());
    return this.get<unknown>(`${USER_PATH}/username/${segment}`).pipe(
      map((res) => normalizeAuthUser(extractData(res))),
    );
  }

  /** GET /api/User/:externalId — perfil por id (quando disponível no backend). */
  getByExternalId(externalId: string): Observable<AuthUser> {
    const segment = encodeURIComponent(externalId.trim());
    return this.get<unknown>(`${USER_PATH}/${segment}`).pipe(
      map((res) => normalizeAuthUser(extractData(res))),
    );
  }

  /** GET /api/User/search?q=… — busca pessoas por texto (nome, @usuário, etc.). */
  searchUsers(q: string): Observable<AuthUser[]> {
    const query = q.trim();
    if (!query) {
      return of([]);
    }
    return this.get<unknown>(`${USER_PATH}/search`, { q: query }).pipe(
      map((res) => extractDataArray<unknown>(res).map((raw) => normalizeAuthUser(raw))),
    );
  }

  /**
   * Deletes the current user account (`DELETE /api/User`).
   */
  deleteUser(): Observable<unknown> {
    return this.delete<unknown>(USER_PATH).pipe(map((res) => extractData(res)));
  }

  /**
   * Updates notification preferences (`PUT /api/User/notifications`).
   */
  updateNotifications(body: UserNotificationPreferences): Observable<unknown> {
    return this.put<unknown>(`${USER_PATH}/notifications`, body).pipe(
      map((res) => extractData(res)),
    );
  }

  /**
   * Updates the current user. Response is expected as `{ data: AuthUser }` or raw user.
   */
  updateUser(body: UpdateUserRequest): Observable<AuthUser> {
    return this.put<unknown>(USER_PATH, body).pipe(
      map((res) => normalizeAuthUser(extractData(res))),
    );
  }

  /**
   * Updates profile picture (`PUT /api/User/profile-picture`).
   * Body: `{ profilePicture }` with raw base64 (no `data:...;base64,` prefix).
   */
  updateProfilePicture(profilePicture: string): Observable<AuthUser> {
    return this.put<unknown>(`${USER_PATH}/profile-picture`, { profilePicture }).pipe(
      map((res) => normalizeAuthUser(extractData(res))),
    );
  }
}
